// filepath: /data/www/go-nvisio-front/src/pages/Query.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import GlobalLayout from '../GlobalLayout';
import ChartBuilder from '../../components/charts/ChartBuilder';
import GridBuilder from '../../components/grids/GridBuilder';
import { createQuery, deleteQuery, getApps, getQuery, getQueries, updateQuery } from '../../services/admin_api';
import { buildQueryImportPlan, buildQueryIni, parseQueryIni } from '../../utils/queryIni';

const normalizeCollection = (response) => {
    if (Array.isArray(response)) {
        return response;
    }

    if (Array.isArray(response?.data)) {
        return response.data;
    }

    return [];
};

const getQueryAppId = (query) => query?.app_id ?? query?.appId ?? query?.app?.id ?? '';

const getQueryType = (query) => query?.category || query?.type || '';

const getAppDisplayLabel = (app) => app?.label || app?.name || app?.code || (app?.id ? `App #${app.id}` : 'App inconnue');

const getQueryDisplayLabel = (query) => query?.label || query?.description || '';

const unwrapEntity = (response) => response?.data && !Array.isArray(response.data) ? response.data : response;

const sanitizeFilenamePart = (value) => String(value || '')
    .trim()
    .replace(/[^a-z0-9_-]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

const downloadTextFile = (filename, content, type = 'text/plain;charset=utf-8') => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

const QUERY_LIST_FILTERS_STORAGE_KEY = 'queryListFilters';

const getStoredQueryListFilters = () => {
    try {
        const storedFilters = localStorage.getItem(QUERY_LIST_FILTERS_STORAGE_KEY);
        if (!storedFilters) {
            return { appId: '', type: '' };
        }

        const parsedFilters = JSON.parse(storedFilters);
        return {
            appId: parsedFilters?.appId ? String(parsedFilters.appId) : '',
            type: parsedFilters?.type ? String(parsedFilters.type) : '',
        };
    } catch (err) {
        console.error('Erreur lors de la lecture des filtres de queries', err);
        return { appId: '', type: '' };
    }
};

const storeQueryListFilters = (appId, type) => {
    try {
        if (!appId && !type) {
            localStorage.removeItem(QUERY_LIST_FILTERS_STORAGE_KEY);
            return;
        }

        localStorage.setItem(QUERY_LIST_FILTERS_STORAGE_KEY, JSON.stringify({ appId, type }));
    } catch (err) {
        console.error('Erreur lors de la sauvegarde des filtres de queries', err);
    }
};

const resolvePreviewCategory = (query) => {
    const category = getQueryType(query);

    if (category === 'grid') {
        return 'grid';
    }

    return category || 'chart';
};

const QueryList = () => {
    const [queries, setQueries] = useState([]);
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedAppId, setSelectedAppId] = useState(() => getStoredQueryListFilters().appId);
    const [selectedType, setSelectedType] = useState(() => getStoredQueryListFilters().type);
    const [exporting, setExporting] = useState(false);
    const importInputRef = useRef(null);
    const [importOpen, setImportOpen] = useState(false);
    const [importFileName, setImportFileName] = useState('');
    const [importQueries, setImportQueries] = useState([]);
    const [importApps, setImportApps] = useState([]);
    const [importRows, setImportRows] = useState([]);
    const [importPreparing, setImportPreparing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState('');
    const [importResult, setImportResult] = useState('');
    const [previewQuery, setPreviewQuery] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState('');
    const [previewRenderKey, setPreviewRenderKey] = useState(0);
    const [queryToDelete, setQueryToDelete] = useState(null);
    const [deletingQueryId, setDeletingQueryId] = useState(null);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            const [queriesResult, appsResult] = await Promise.allSettled([
                getQueries(1, 1000),
                getApps(1, 1000)
            ]);

            if (queriesResult.status === 'fulfilled') {
                setQueries(normalizeCollection(queriesResult.value));
                setError('');
            } else {
                console.error(queriesResult.reason);
                setQueries([]);
                setError('Erreur lors de la récupération des queries.');
            }

            if (appsResult.status === 'fulfilled') {
                setApps(normalizeCollection(appsResult.value));
            } else {
                console.error('Erreur lors de la récupération des apps', appsResult.reason);
                setApps([]);
            }

            setLoading(false);
        };

        fetchData();
    }, []);

    useEffect(() => {
        storeQueryListFilters(selectedAppId, selectedType);
    }, [selectedAppId, selectedType]);

    const typeOptions = [...new Set(
        queries
            .map((query) => getQueryType(query))
            .filter(Boolean)
    )].sort((firstType, secondType) => firstType.localeCompare(secondType));

    const filteredQueries = queries.filter((query) => {
        const queryAppId = String(getQueryAppId(query));
        const queryType = getQueryType(query);

        if (selectedAppId && queryAppId !== String(selectedAppId)) {
            return false;
        }

        if (selectedType && queryType !== selectedType) {
            return false;
        }

        return true;
    });

    const activeFiltersCount = [selectedAppId, selectedType].filter(Boolean).length;

    const resolveAppLabel = (query) => {
        if (query?.app?.label || query?.app?.name || query?.app?.code) {
            return getAppDisplayLabel(query.app);
        }

        const matchedApp = apps.find((app) => String(app.id) === String(getQueryAppId(query)));
        if (matchedApp) {
            return getAppDisplayLabel(matchedApp);
        }

        return getQueryAppId(query) ? `App #${getQueryAppId(query)}` : 'Non renseignée';
    };

    const buildExportFilename = () => {
        const parts = ['queries'];
        if (selectedAppId) {
            const selectedApp = apps.find((app) => String(app.id) === String(selectedAppId));
            parts.push(sanitizeFilenamePart(getAppDisplayLabel(selectedApp || { id: selectedAppId })));
        }
        if (selectedType) {
            parts.push(sanitizeFilenamePart(selectedType));
        }
        parts.push(new Date().toISOString().slice(0, 10));
        return `${parts.filter(Boolean).join('-')}.ini`;
    };

    const buildIniContent = (queriesToExport) => {
        const selectedApp = selectedAppId
            ? apps.find((app) => String(app.id) === String(selectedAppId))
            : null;
        const filterLabel = [
            selectedAppId ? `app=${getAppDisplayLabel(selectedApp || { id: selectedAppId })}` : 'app=*',
            selectedType ? `type=${selectedType}` : 'type=*'
        ].join(', ');

        return buildQueryIni(queriesToExport, { filterLabel, resolveAppLabel });
    };

    const handleExportIni = async () => {
        if (filteredQueries.length === 0 || exporting) {
            return;
        }

        setExporting(true);
        try {
            const queriesToExport = await Promise.all(filteredQueries.map(async (query) => {
                if (query.template !== undefined || !query.id) {
                    return query;
                }

                try {
                    return unwrapEntity(await getQuery(query.id));
                } catch (err) {
                    console.error(`Erreur lors du chargement de la query ${query.id}`, err);
                    return query;
                }
            }));

            const incompleteQuery = queriesToExport.find((query) => !query.name || !getQueryType(query) || !String(query.template ?? '').trim());
            if (incompleteQuery) {
                setError(`Export impossible : la query « ${incompleteQuery.name || incompleteQuery.id} » n’a pas de nom, de catégorie ou de template complet.`);
                return;
            }

            setError('');
            downloadTextFile(buildExportFilename(), buildIniContent(queriesToExport), 'text/plain;charset=utf-8');
        } finally {
            setExporting(false);
        }
    };

    const handleImportFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        setImportOpen(true);
        setImportFileName(file.name);
        setImportQueries([]);
        setImportRows([]);
        setImportError('');
        setImportResult('');
        setImportPreparing(true);

        try {
            const parsed = parseQueryIni(await file.text());
            const [availableApps, existingQueries] = await Promise.all([
                getApps(1, 1000),
                getQueries(1, 10000),
            ]);
            const appsList = normalizeCollection(availableApps);
            const plan = buildQueryImportPlan(parsed, appsList, normalizeCollection(existingQueries), selectedAppId);
            setImportQueries(parsed);
            setImportApps(appsList);
            setImportRows(plan);
        } catch (err) {
            setImportError(err?.message || 'Impossible de lire le fichier INI.');
        } finally {
            setImportPreparing(false);
        }
    };

    const handleCloseImport = () => {
        if (importing || importPreparing) return;
        setImportOpen(false);
    };

    const handleConfirmImport = async () => {
        if (importing || importPreparing || importRows.length === 0 || importRows.some((row) => row.action === 'error')) return;
        setImporting(true);
        setImportError('');
        let completed = 0;
        let currentRow = null;

        try {
            const existingQueries = normalizeCollection(await getQueries(1, 10000));
            const currentPlan = buildQueryImportPlan(importQueries, importApps, existingQueries, selectedAppId);
            const changed = currentPlan.some((row, index) => (
                row.action !== importRows[index]?.action || String(row.existingId || '') !== String(importRows[index]?.existingId || '')
            ));
            if (changed) {
                setImportRows(currentPlan);
                setImportError('Les queries ont changé depuis l’aperçu. Vérifiez le nouveau plan avant de confirmer.');
                return;
            }

            for (const row of currentPlan) {
                currentRow = row;
                if (row.action === 'update') {
                    await updateQuery(row.existingId, row.payload);
                } else {
                    await createQuery(row.payload);
                }
                completed += 1;
            }

            const created = currentPlan.filter((row) => row.action === 'create').length;
            setImportResult(`${created} query(s) créées, ${currentPlan.length - created} mise(s) à jour depuis ${importFileName}.`);
            setImportOpen(false);
        } catch (err) {
            const details = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Erreur inconnue';
            setImportError(`${completed}/${importRows.length} query(s) enregistrées. Échec sur « ${currentRow?.query.name || 'chargement'} » : ${String(details)}.`);
            if (completed > 0) {
                try {
                    const latest = normalizeCollection(await getQueries(1, 10000));
                    setImportRows(buildQueryImportPlan(importQueries, importApps, latest, selectedAppId));
                } catch (refreshError) {
                    console.error('Erreur lors de l’actualisation du plan d’import', refreshError);
                    setImportRows([]);
                }
            }
        } finally {
            try {
                setQueries(normalizeCollection(await getQueries(1, 1000)));
            } catch (refreshError) {
                console.error('Erreur lors de l’actualisation des queries', refreshError);
            }
            setImporting(false);
        }
    };

    const handleOpenPreview = async (query) => {
        setPreviewQuery(query);
        setPreviewError('');
        setPreviewLoading(true);
        setPreviewRenderKey((currentKey) => currentKey + 1);

        try {
            if (query?.id && (query.template === undefined || !getQueryAppId(query) || !getQueryType(query))) {
                const detailedQuery = unwrapEntity(await getQuery(query.id));
                setPreviewQuery(detailedQuery || query);
            }
        } catch (err) {
            console.error(`Erreur lors du chargement de la query ${query?.id}`, err);
            setPreviewError('Impossible de charger le détail de cette query.');
        } finally {
            setPreviewLoading(false);
            setPreviewRenderKey((currentKey) => currentKey + 1);
        }
    };

    const handleClosePreview = () => {
        setPreviewQuery(null);
        setPreviewError('');
        setPreviewLoading(false);
    };

    const handleOpenDeleteConfirm = (query) => {
        setQueryToDelete(query);
        setDeleteError('');
    };

    const handleCloseDeleteConfirm = () => {
        if (deletingQueryId) {
            return;
        }

        setQueryToDelete(null);
        setDeleteError('');
    };

    const handleConfirmDelete = async () => {
        if (!queryToDelete?.id || deletingQueryId) {
            return;
        }

        setDeleteError('');
        setDeletingQueryId(queryToDelete.id);

        try {
            await deleteQuery(queryToDelete.id);
            setQueries((currentQueries) => currentQueries.filter((query) => String(query.id) !== String(queryToDelete.id)));

            if (previewQuery && String(previewQuery.id) === String(queryToDelete.id)) {
                handleClosePreview();
            }

            setQueryToDelete(null);
        } catch (err) {
            console.error(`Erreur lors de la suppression de la query ${queryToDelete.id}`, err);
            setDeleteError('Impossible de supprimer cette query.');
        } finally {
            setDeletingQueryId(null);
        }
    };

    const renderPreviewContent = () => {
        if (previewLoading) {
            return (
                <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: 360 }}>
                    <CircularProgress />
                    <Typography color="text.secondary">Chargement de l’aperçu...</Typography>
                </Stack>
            );
        }

        if (previewError) {
            return <Alert severity="error">{previewError}</Alert>;
        }

        if (!previewQuery) {
            return null;
        }

        const previewAppId = getQueryAppId(previewQuery);
        const previewCategory = resolvePreviewCategory(previewQuery);

        if (!previewAppId) {
            return (
                <Alert severity="warning">
                    L’aperçu nécessite un app_id renseigné sur la query.
                </Alert>
            );
        }

        if (!previewQuery.name) {
            return (
                <Alert severity="warning">
                    L’aperçu nécessite un nom de query.
                </Alert>
            );
        }

        if (previewCategory === 'grid') {
            return (
                <Box sx={{ height: 620, minHeight: 420 }}>
                    <GridBuilder
                        key={`preview-grid-${previewRenderKey}-${previewQuery.id || previewQuery.name}`}
                        app={previewAppId}
                        view="ssrm"
                        query={previewQuery.name}
                        queryId={previewQuery.id}
                    />
                </Box>
            );
        }

        const previewView = previewCategory === 'indic' ? 'indic' : 'bar';

        return (
            <Box sx={{ height: 620, minHeight: 420 }}>
                <ChartBuilder
                    key={`preview-${previewCategory}-${previewRenderKey}-${previewQuery.id || previewQuery.name}`}
                    app={previewAppId}
                    view={previewView}
                    query={previewQuery.name}
                    category={previewCategory}
                    scale={previewCategory === 'indic' ? 1 : 0.75}
                />
            </Box>
        );
    };

    return (
        <GlobalLayout>
            <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1400, mx: 'auto' }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 3 },
                        mb: 3,
                        borderRadius: 3,
                        color: '#102a43',
                        background: 'linear-gradient(135deg, #f4f8fb 0%, #e7f1f7 100%)',
                        border: '1px solid #d9e7f0'
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={2}
                    >
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                Liste des Queries
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#486581' }}>
                                Parcourez les requêtes par application et par type, puis accédez rapidement à l’édition.
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            component={Link}
                            to="/query/create"
                            sx={{ px: 2.5, py: 1, borderRadius: 2, boxShadow: 'none' }}
                        >
                            Nouvelle Query
                        </Button>
                    </Stack>
                </Paper>

                {importResult && (
                    <Alert severity="success" onClose={() => setImportResult('')} sx={{ mb: 3 }}>
                        {importResult}
                    </Alert>
                )}

                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 3 }}>
                    <Paper elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                        <Typography variant="overline" sx={{ color: '#7b8794' }}>
                            Total
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {queries.length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#52606d' }}>
                            queries chargées
                        </Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                        <Typography variant="overline" sx={{ color: '#7b8794' }}>
                            Résultat
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {filteredQueries.length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#52606d' }}>
                            après filtrage
                        </Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                        <Typography variant="overline" sx={{ color: '#7b8794' }}>
                            Filtres actifs
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {activeFiltersCount}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#52606d' }}>
                            App / Type
                        </Typography>
                    </Paper>
                </Stack>

                <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="query-app-filter-label">App</InputLabel>
                            <Select
                                labelId="query-app-filter-label"
                                value={selectedAppId}
                                label="App"
                                onChange={(event) => setSelectedAppId(event.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Toutes les apps</em>
                                </MenuItem>
                                {apps.map((app) => (
                                    <MenuItem key={app.id} value={String(app.id)}>
                                        {getAppDisplayLabel(app)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel id="query-type-filter-label">Type</InputLabel>
                            <Select
                                labelId="query-type-filter-label"
                                value={selectedType}
                                label="Type"
                                onChange={(event) => setSelectedType(event.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Tous les types</em>
                                </MenuItem>
                                {typeOptions.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            variant="outlined"
                            onClick={() => {
                                setSelectedAppId('');
                                setSelectedType('');
                            }}
                            disabled={!selectedAppId && !selectedType}
                            sx={{ minWidth: { md: 180 }, height: 40 }}
                        >
                            Réinitialiser
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<FileUploadOutlinedIcon />}
                            onClick={() => importInputRef.current?.click()}
                            disabled={loading || importing || importPreparing}
                            sx={{ minWidth: { md: 210 }, height: 40 }}
                        >
                            Importer .ini
                        </Button>
                        <input
                            ref={importInputRef}
                            type="file"
                            accept=".ini,text/plain"
                            aria-label="Fichier INI de queries"
                            hidden
                            onChange={handleImportFile}
                        />
                        <Button
                            variant="contained"
                            startIcon={<FileDownloadOutlinedIcon />}
                            onClick={handleExportIni}
                            disabled={loading || exporting || filteredQueries.length === 0}
                            sx={{ minWidth: { md: 210 }, height: 40, boxShadow: 'none' }}
                        >
                            {exporting ? 'Export...' : 'Exporter .ini'}
                        </Button>
                    </Stack>

                    {activeFiltersCount > 0 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                            {selectedAppId && (
                                <Chip
                                    label={`App: ${getAppDisplayLabel(apps.find((app) => String(app.id) === String(selectedAppId)) || { id: selectedAppId })}`}
                                    onDelete={() => setSelectedAppId('')}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {selectedType && (
                                <Chip
                                    label={`Type: ${selectedType}`}
                                    onDelete={() => setSelectedType('')}
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                        </Stack>
                    )}
                </Paper>

                {loading && (
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                        <Typography>Chargement des queries...</Typography>
                    </Paper>
                )}

                {!loading && error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Nom</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>App</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredQueries.length > 0 ? (
                                    filteredQueries.map((query) => (
                                        <TableRow key={query.id} hover>
                                            <TableCell sx={{ whiteSpace: 'nowrap', color: '#52606d' }}>
                                                #{query.id}
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontWeight: 600, color: '#102a43' }}>
                                                    {query.name || 'Sans nom'}
                                                </Typography>
                                                {getQueryDisplayLabel(query) && (
                                                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                                        {getQueryDisplayLabel(query)}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ color: '#334e68' }}>
                                                {resolveAppLabel(query)}
                                            </TableCell>
                                            <TableCell>
                                                {getQueryType(query) ? (
                                                    <Chip label={getQueryType(query)} size="small" sx={{ textTransform: 'capitalize' }} />
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: '#9aa5b1' }}>
                                                        Non renseigné
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    <Tooltip title="Afficher un aperçu">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                                                            onClick={() => handleOpenPreview(query)}
                                                        >
                                                            Aperçu
                                                        </Button>
                                                    </Tooltip>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        component={Link}
                                                        to={`/query/edit/${query.id}`}
                                                    >
                                                        Éditer
                                                    </Button>
                                                    <Tooltip title="Supprimer la query">
                                                        <span>
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                size="small"
                                                                startIcon={<DeleteOutlineIcon fontSize="small" />}
                                                                onClick={() => handleOpenDeleteConfirm(query)}
                                                                disabled={!query.id || deletingQueryId === query.id}
                                                            >
                                                                Supprimer
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ py: 5, textAlign: 'center' }}>
                                            <Typography sx={{ fontWeight: 600, color: '#102a43', mb: 0.5 }}>
                                                Aucune query trouvée
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                                Ajustez les filtres App / Type pour élargir la recherche.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Dialog open={importOpen} onClose={handleCloseImport} maxWidth="md" fullWidth>
                    <DialogTitle>Importer des queries depuis un fichier .ini</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {importFileName} · Les queries sont associées à leur application, leur nom et leur catégorie.
                            Un même nom et une même catégorie peuvent exister dans plusieurs applications.
                            Les identifiants du fichier ne sont pas utilisés pour les mises à jour.
                        </Typography>
                        {importPreparing && <CircularProgress size={24} />}
                        {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}
                        {importRows.length > 0 && (
                            <>
                                <Typography sx={{ mb: 1 }}>
                                    {importRows.filter((row) => row.action === 'create').length} création(s),{' '}
                                    {importRows.filter((row) => row.action === 'update').length} mise(s) à jour,{' '}
                                    {importRows.filter((row) => row.action === 'error').length} erreur(s).
                                </Typography>
                                <TableContainer sx={{ maxHeight: 360 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Query</TableCell>
                                                <TableCell>App</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {importRows.map((row, index) => (
                                                <TableRow key={`${row.query.section}-${index}`}>
                                                    <TableCell>{row.query.name}</TableCell>
                                                    <TableCell>{row.app ? getAppDisplayLabel(row.app) : row.query.app || '—'}</TableCell>
                                                    <TableCell>{row.query.category}</TableCell>
                                                    <TableCell>
                                                        {row.action === 'error'
                                                            ? <Typography color="error" variant="body2">{row.reason}</Typography>
                                                            : row.action === 'update' ? 'Mise à jour' : 'Création'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseImport} disabled={importPreparing || importing}>Annuler</Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmImport}
                            disabled={importPreparing || importing || importRows.length === 0 || importRows.some((row) => row.action === 'error')}
                        >
                            {importing ? 'Import en cours...' : `Confirmer l’import (${importRows.length})`}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={Boolean(previewQuery)}
                    onClose={handleClosePreview}
                    maxWidth="xl"
                    fullWidth
                    PaperProps={{
                        sx: {
                            height: '92vh',
                            maxHeight: '92vh',
                            borderRadius: 2,
                        }
                    }}
                >
                    <DialogTitle sx={{ pr: 7 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Aperçu de la query
                            </Typography>
                            {previewQuery && (
                                <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                    {previewQuery.name}
                                    {getQueryType(previewQuery) ? ` · ${getQueryType(previewQuery)}` : ''}
                                    {getQueryAppId(previewQuery) ? ` · ${resolveAppLabel(previewQuery)}` : ''}
                                </Typography>
                            )}
                        </Box>
                        <IconButton
                            aria-label="Fermer"
                            onClick={handleClosePreview}
                            sx={{ position: 'absolute', right: 12, top: 12 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers sx={{ p: 2 }}>
                        {renderPreviewContent()}
                    </DialogContent>
                    <DialogActions>
                        {previewQuery?.id && (
                            <Button component={Link} to={`/query/edit/${previewQuery.id}`} onClick={handleClosePreview}>
                                Éditer
                            </Button>
                        )}
                        <Button onClick={handleClosePreview} variant="contained" sx={{ boxShadow: 'none' }}>
                            Fermer
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={Boolean(queryToDelete)}
                    onClose={handleCloseDeleteConfirm}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Supprimer la query</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ mb: 2 }}>
                            Voulez-vous vraiment supprimer cette query ?
                        </Typography>
                        {queryToDelete && (
                            <Paper elevation={0} sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2, backgroundColor: '#f8fafc' }}>
                                <Typography sx={{ fontWeight: 700 }}>
                                    {queryToDelete.name || `Query #${queryToDelete.id}`}
                                </Typography>
                                {getQueryDisplayLabel(queryToDelete) && (
                                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                        {getQueryDisplayLabel(queryToDelete)}
                                    </Typography>
                                )}
                            </Paper>
                        )}
                        {deleteError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {deleteError}
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDeleteConfirm} disabled={Boolean(deletingQueryId)}>
                            Annuler
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            color="error"
                            variant="contained"
                            disabled={Boolean(deletingQueryId)}
                            sx={{ boxShadow: 'none' }}
                        >
                            {deletingQueryId ? 'Suppression...' : 'Supprimer'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </GlobalLayout>
    );
};

export default QueryList;
